// src/components/employee/JobDetails.js - Updated with Delete Equipment Button and consistent card sizes
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  IconButton,
  AppBar,
  Toolbar,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
  List,
  ListItem,
  ListItemText,
  Chip,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Snackbar,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  PhotoCamera as CameraIcon,
  CalendarToday as CalendarIcon,
  Room as LocationIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  GetApp as DownloadIcon,
  Visibility as ViewIcon,
} from "@mui/icons-material";
import { format } from "date-fns";

const JobDetails = () => {
  const { id: jobId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [error, setError] = useState("");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [equipmentToDelete, setEquipmentToDelete] = useState(null);

  // Action menu state
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedEquipment, setSelectedEquipment] = useState(null);

  useEffect(() => {
    fetchJobDetails();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      setLoading(true);
      // Fetch job details
      const jobRes = await axios.get(`/jobs/${jobId}`);
      setJob(jobRes.data.job);

      // Fetch equipment for this job
      const equipmentRes = await axios.get(`/equipment/job/${jobId}`);
      setEquipment(equipmentRes.data.equipment);

      setError("");
    } catch (error) {
      setError("Failed to load job details. Please try again.");
      console.error("Error fetching job details:", error);
    } finally {
      setLoading(false);
    }
  };

  const goBack = () => {
    navigate("/");
  };

  const handleCaptureClick = () => {
    navigate(`/job/${jobId}/capture`);
  };

  const openEquipmentMenu = (event, equipment) => {
    setMenuAnchorEl(event.currentTarget);
    setSelectedEquipment(equipment);
  };

  const closeEquipmentMenu = () => {
    setMenuAnchorEl(null);
    setSelectedEquipment(null);
  };

  const handleDeleteClick = (equipment) => {
    setEquipmentToDelete(equipment);
    setDeleteDialogOpen(true);
    closeEquipmentMenu();
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setEquipmentToDelete(null);
  };

  const confirmDelete = async () => {
    if (!equipmentToDelete) return;

    try {
      await axios.delete(`/equipment/${equipmentToDelete._id}`);

      // Remove the deleted equipment from the state
      setEquipment(
        equipment.filter((item) => item._id !== equipmentToDelete._id)
      );

      // Show success message
      setSnackbarMessage("Equipment deleted successfully");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error deleting equipment:", error);
      setError("Failed to delete equipment. Please try again.");
    } finally {
      closeDeleteDialog();
    }
  };

  const handleViewImage = (imageUrl) => {
    window.open(`/api/uploads/${imageUrl}`, "_blank");
    closeEquipmentMenu();
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "MMMM dd, yyyy");
    } catch (error) {
      return "Invalid Date";
    }
  };

  const exportToCsv = () => {
    if (!equipment || equipment.length === 0) {
      setSnackbarMessage("No equipment data to export");
      setSnackbarOpen(true);
      return;
    }

    // Create CSV header row
    const headers = [
      "Brand",
      "Model Number",
      "Serial Number",
      "Manufactured Date",
      "Captured On",
    ];

    // Create rows for each equipment
    const rows = equipment.map((item) => [
      item.brand || "Not detected",
      item.modelNumber || "Not detected",
      item.serialNumber || "Not detected",
      item.manufacturedDate || "Not detected",
      formatDate(item.createdAt),
    ]);

    // Combine headers and rows
    const csvContent = [
      headers.join(","),
      ...rows.map((row) => row.join(",")),
    ].join("\n");

    // Create a Blob and download link
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", `job_${jobId}_equipment.csv`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setSnackbarMessage("CSV exported successfully");
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          <IconButton
            size="large"
            edge="start"
            color="inherit"
            sx={{ mr: 2 }}
            onClick={goBack}
          >
            <BackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Job Details
          </Typography>
          <Button
            color="inherit"
            startIcon={<CameraIcon />}
            onClick={handleCaptureClick}
          >
            Capture
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {job && (
          <>
            <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
              <Typography variant="h5" gutterBottom>
                {job.name}
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 2,
                  color: "text.secondary",
                }}
              >
                <CalendarIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1">{formatDate(job.date)}</Typography>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mt: 1,
                  color: "text.secondary",
                }}
              >
                <LocationIcon fontSize="small" sx={{ mr: 1 }} />
                <Typography variant="body1">{job.address}</Typography>
              </Box>

              {job.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    gutterBottom
                  >
                    Description:
                  </Typography>
                  <Typography variant="body1">{job.description}</Typography>
                </Box>
              )}

              <Box sx={{ mt: 2 }}>
                <Chip
                  label={job.status.toUpperCase()}
                  color={
                    job.status === "completed"
                      ? "success"
                      : job.status === "in-progress"
                      ? "info"
                      : "warning"
                  }
                  sx={{ textTransform: "capitalize" }}
                />
              </Box>
            </Paper>

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 2,
              }}
            >
              <Typography variant="h6">Captured Equipment</Typography>
              <Box>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleCaptureClick}
                >
                  Add Equipment
                </Button>
              </Box>
            </Box>

            {equipment.length === 0 ? (
              <Alert severity="info">
                No equipment has been captured for this job yet. Use the "Add
                Equipment" button to get started.
              </Alert>
            ) : (
              <Grid container spacing={2}>
                {equipment.map((item) => (
                  <Grid item xs={12} sm={6} md={3} key={item._id}>
                    <Card
                      sx={{
                        height: "100%",
                        display: "flex",
                        flexDirection: "column",
                        width: "100%", // Ensure cards take full width of grid item
                      }}
                    >
                      <CardContent sx={{ flexGrow: 1, padding: 2 }}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            marginBottom: 1,
                          }}
                        >
                          <Typography
                            variant="h6"
                            gutterBottom
                            sx={{
                              width: "calc(100% - 40px)", // Leave space for the menu button
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            }}
                          >
                            {item.brand || "Unknown Brand"}{" "}
                            {item.modelNumber || ""}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={(e) => openEquipmentMenu(e, item)}
                            sx={{ flexShrink: 0 }}
                          >
                            <MoreVertIcon />
                          </IconButton>
                        </Box>

                        <Divider sx={{ my: 1.5 }} />

                        <List dense disablePadding>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Serial Number"
                              secondary={item.serialNumber || "Not detected"}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  fontWeight: "bold",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Model Number"
                              secondary={item.modelNumber || "Not detected"}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  fontWeight: "bold",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Brand"
                              secondary={item.brand || "Not detected"}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  fontWeight: "bold",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Manufactured Date"
                              secondary={
                                item.manufacturedDate || "Not detected"
                              }
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  fontWeight: "bold",
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                          <ListItem sx={{ py: 0.5 }}>
                            <ListItemText
                              primary="Captured On"
                              secondary={formatDate(item.createdAt)}
                              primaryTypographyProps={{
                                variant: "body2",
                                sx: { fontWeight: 500 },
                              }}
                              secondaryTypographyProps={{
                                variant: "body2",
                                sx: {
                                  whiteSpace: "nowrap",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                },
                              }}
                            />
                          </ListItem>
                        </List>

                        {item.images && item.images.length > 0 && (
                          <Box sx={{ mt: 2, textAlign: "center" }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() =>
                                window.open(
                                  `/api/uploads/${item.images[0]}`,
                                  "_blank"
                                )
                              }
                            >
                              View Image
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </>
        )}
      </Container>

      {/* Equipment Actions Menu */}
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={closeEquipmentMenu}
      >
        {selectedEquipment?.images?.length > 0 && (
          <MenuItem
            onClick={() => handleViewImage(selectedEquipment.images[0])}
          >
            <ListItemText primary="View Image" />
            <ViewIcon fontSize="small" sx={{ ml: 1 }} />
          </MenuItem>
        )}
        <MenuItem onClick={() => handleDeleteClick(selectedEquipment)}>
          <ListItemText primary="Delete Equipment" />
          <DeleteIcon fontSize="small" sx={{ ml: 1, color: "error.main" }} />
        </MenuItem>
      </Menu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this equipment record? This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDeleteDialog}>Cancel</Button>
          <Button onClick={confirmDelete} color="error">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />
    </Box>
  );
};

export default JobDetails;
